import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heart, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';
import axios from 'axios';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(false);

    try {
      const userData = await login(email, password);
      toast.success(copy.login.successToast);
      navigate(userData.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === 'email_not_verified') {
        setUnverified(true);
      } else {
        const message = error.response?.data?.detail || copy.login.errorToast;
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, { email });
      toast.success(copy.verification.resendSuccess);
    } catch {
      toast.success(copy.verification.resendSuccess);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Left side - Image (hidden on mobile) */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1678977978610-7f989f995edd?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#1D2D44]/60"></div>
        <div className="relative z-10 flex flex-col justify-center items-center text-center p-12">
          <h2 className="font-serif text-4xl text-white mb-4">{copy.login.welcomeBack}</h2>
          <p className="text-white/80 text-lg max-w-md leading-relaxed">
            {copy.login.welcomeBackDescription}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-[#424E59] hover:text-[#1D2D44] mb-8 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {copy.nav.backToHome}
          </Link>

          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-serif text-2xl text-[#1D2D44]">
                Our Love <Heart className="inline-block w-5 h-5 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
              </h1>
            </Link>
          </div>

          <Card className="border-none shadow-lg rounded-2xl">
            <CardHeader className="text-center pb-2">
              <h2 className="font-serif text-2xl text-[#1D2D44]">{copy.login.title}</h2>
              <p className="text-[#424E59] text-sm mt-2">
                {copy.login.subtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#3A4A5A]">{copy.login.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={copy.login.emailPlaceholder}
                    required
                    data-testid="login-email-input"
                    className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#3A4A5A]">{copy.login.passwordLabel}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={copy.login.passwordPlaceholder}
                      required
                      data-testid="login-password-input"
                      className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#424E59] hover:text-[#3A4A5A]"
                      data-testid="toggle-password-visibility"
                      aria-label={copy.a11y.togglePasswordVisibility}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full h-12 text-base font-medium shadow-lg transition-all duration-300 hover:scale-[1.01]"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
                      {copy.login.submitting}
                    </span>
                  ) : (
                    copy.login.submitButton
                  )}
                </Button>

                {unverified && (
                  <div className="bg-[#B89B5E]/10 border border-[#B89B5E]/30 rounded-xl p-4 mt-4" data-testid="unverified-banner">
                    <p className="text-[#1D2D44] text-sm mb-3">{copy.verification.loginBlocked}</p>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      className="text-sm text-[#B89B5E] hover:text-[#1D2D44] font-medium transition-colors"
                      data-testid="resend-verification-btn"
                    >
                      {resending ? copy.verification.resending : copy.verification.resendLink}
                    </button>
                  </div>
                )}

                <div className="text-center mt-3">
                  <Link to="/recover" className="text-sm text-[#424E59] hover:text-[#B89B5E] transition-colors" data-testid="forgot-password-link">
                    {copy.recovery.forgotLink}
                  </Link>
                </div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-[#424E59] text-sm">
                  {copy.login.noAccount}{' '}
                  <Link 
                    to="/register" 
                    className="text-[#1D2D44] font-medium hover:text-[#5E7F63] transition-colors"
                    data-testid="go-to-register-link"
                  >
                    {copy.login.createOne}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
